import React, { useState, useEffect, useMemo } from 'react';

import Box from '@mui/material/Box';

import {
  ContentBlock,
  DisplayConceptRef,
  DisplayProduct,
  InlineElement,
  StructuredDoc,
} from '@/lib/api';
import { API_URL } from '@/lib/consts';

import { StructuredDocRenderContext } from './StructuredDocRenderContext';
import { ContentBlockRenderer } from './blocks/ContentBlockRenderer';

interface StructuredDocRendererProps {
  document: StructuredDoc;
  concepts: DisplayConceptRef[];
}

export const StructuredDocRenderer: React.FC<StructuredDocRendererProps> = ({
  document,
  concepts,
}) => {
  const [productDetails, setProductDetails] = useState<Map<string, DisplayProduct>>(new Map());

  const productCrefs = useMemo(() => {
    return concepts.filter((c) => c.is_product).map((c) => c.cref);
  }, [concepts]);

  // TODO(1): If the code below is any indication, we may end up writing a number of
  // these StructuredDoc tree traversals; we should see if we can create a utility to
  // make that easy, concise, and error-free. Note: extractConceptRefs() in
  // ContentBlockRenderer.tsx is essentially a duplicate of extractInlineCrefs() below.

  // Floating card deduplication works at two levels:
  // 1. Within-paragraph: handled in ParagraphRenderer, which aggregates and dedups
  //    all mentions in the paragraph and runs paragraph-level floating mini-card
  //    rendering where appropriate
  // 2. Cross-block: computed here via two passes:
  //    a. Table suppression: products in any product_comparison_table never get floating cards
  //    b. Paragraph deduplication: for products NOT in tables, only the first paragraph
  //       mentioning them gets a floating card
  //
  // Block identity (===) comparison works because we pass the same block objects through rendering.
  const { productsInTables, firstParagraphByCref } = useMemo(() => {
    // Only track products (not topics or other concept types)
    const productSet = new Set(concepts.filter((c) => c.is_product).map((c) => c.cref));

    // Extract all concept_link crefs from inline elements, recursing into nested elements
    const extractInlineCrefs = (elements: InlineElement[]): string[] => {
      const refs: string[] = [];
      for (const el of elements) {
        if (el.type === 'concept_link') {
          refs.push(el.cref);
          if (Array.isArray(el.text)) {
            refs.push(...extractInlineCrefs(el.text));
          }
        } else if ('content' in el && Array.isArray(el.content)) {
          refs.push(...extractInlineCrefs(el.content as InlineElement[]));
        } else if ('text' in el && Array.isArray(el.text)) {
          refs.push(...extractInlineCrefs(el.text as InlineElement[]));
        }
      }
      return refs;
    };

    // First pass: collect all products that appear in any product_comparison_table
    const inTables = new Set<string>();
    const collectTableProducts = (block: ContentBlock) => {
      switch (block.type) {
        case 'product_comparison_table': {
          for (const row of block.rows) {
            if (productSet.has(row.product_cref)) {
              inTables.add(row.product_cref);
            }
          }
          break;
        }
        case 'list': {
          for (const item of block.items) {
            for (const child of item.blocks) collectTableProducts(child);
          }
          break;
        }
        case 'block_quote': {
          for (const child of block.blocks) collectTableProducts(child);
          break;
        }
      }
    };
    for (const b of document.blocks) collectTableProducts(b);

    // Second pass: for products NOT in tables, find the first paragraph mentioning each
    const firstParagraph = new Map<string, ContentBlock>();
    // TODO(2): If regular 'table' blocks ever contain concept_link elements in their
    // cells, we'll need to handle them here as well.
    const assignFirstParagraph = (block: ContentBlock) => {
      switch (block.type) {
        case 'paragraph': {
          const refs = extractInlineCrefs(block.inline_elements);
          for (const cref of refs) {
            if (productSet.has(cref) && !firstParagraph.has(cref)) {
              firstParagraph.set(cref, block);
            }
          }
          break;
        }
        case 'list': {
          for (const item of block.items) {
            for (const child of item.blocks) assignFirstParagraph(child);
          }
          break;
        }
        case 'block_quote': {
          for (const child of block.blocks) assignFirstParagraph(child);
          break;
        }
      }
    };
    for (const b of document.blocks) assignFirstParagraph(b);

    return { productsInTables: inTables, firstParagraphByCref: firstParagraph };
  }, [document, concepts]);

  useEffect(() => {
    const fetchProductDetails = async () => {
      const newProductDetails = new Map<string, DisplayProduct>();

      await Promise.all(
        productCrefs.map(async (cref) => {
          try {
            const response = await fetch(`${API_URL}/product/${encodeURIComponent(cref)}`);
            if (response.ok) {
              const product = (await response.json()) as DisplayProduct;
              newProductDetails.set(cref, product);
            }
          } catch (error) {
            console.error(`Failed to fetch product ${cref}:`, error);
          }
        })
      );

      setProductDetails(newProductDetails);
    };

    if (productCrefs.length > 0) {
      fetchProductDetails();
    }
  }, [productCrefs]);

  // Provide dedup decision function to nested renderers via context (avoids prop drilling)
  // Two explicit checks: (1) not in any table, (2) this is the first paragraph mentioning it
  return (
    <StructuredDocRenderContext.Provider
      value={{
        shouldShowFloatingCard: (cref, block) =>
          !productsInTables.has(cref) && firstParagraphByCref.get(cref) === block,
      }}
    >
      <Box className="structured-doc">
        {document.blocks.map((block, index) => (
          <ContentBlockRenderer
            key={index}
            block={block}
            concepts={concepts}
            productDetails={productDetails}
          />
        ))}
      </Box>
    </StructuredDocRenderContext.Provider>
  );
};
